import { forwardRef, memo, useMemo, useState } from "react";
import clsx from "clsx";

import classes from "./styles.module.scss";

import { Typography } from "@components/atoms/typography";
import type { ICheckboxProps } from "@components/atoms/checkbox/types";
import { Icon } from "@/components/atoms/icons";

export const Checkbox = memo(
  forwardRef<HTMLInputElement, ICheckboxProps>(function Checkbox(props, ref) {
    const {
      value,
      onChange,
      className,
      id,
      noTitle,
      name,
      title,
      disabled,
      readonly,
      htmlProps,
      onClick,
    } = props;

    const [checked, setChecked] = useState(false);

    const [inputValue, inputOnChange] = useMemo(() => {
      return [value ?? checked, onChange ?? setChecked];
    }, [checked, onChange, value]);

    const inputId = useMemo(() => `id_${title}${id}`, [id, title]);

    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <label
        id={id || "checkbox_id"}
        className={clsx(classes.checkbox, className)}
        htmlFor={inputId}
        onClick={onClick}
      >
        <input
          ref={ref}
          type={"checkbox"}
          checked={inputValue}
          id={inputId}
          name={name}
          disabled={disabled}
          {...htmlProps}
          className={classes.checkbox__input}
          onChange={() => {
            if (disabled || readonly) {
              return;
            }
            inputOnChange(!inputValue);
          }}
        />
        <div className={clsx(classes.checkbox__content)}>
          <div
            className={clsx(
              classes.checkbox__box,
              inputValue && classes.checkbox__boxSelected,
              disabled && classes.checkbox__boxDisabled,
            )}
          >
            {inputValue && !disabled && (
              <Icon type={"linear"} name={"Tick"} color={"white"} />
            )}
          </div>
          <Typography
            variant={"description1"}
            fontWeight={"semi-bold"}
            color={props?.color || "primary-text"}
          >
            {!noTitle && title}
          </Typography>
        </div>
      </label>
    );
  }),
);

Checkbox.displayName = "Checkbox";
